import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { parseMode } from './lib/parseMode';
import { recentsStore } from './lib/recentsStore';
import { PaletteApiContext, PaletteContext, type PaletteApiValue, type PaletteContextValue } from './palette-context';
import type { PaletteActionDescriptor, RecentEntity } from './types';

export function PaletteProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
    const [open, setOpenState] = useState(false);
    const [rawQuery, setRawQuery] = useState('');
    const [activeView, setActiveView] = useState<string | null>(null);
    const [viewInitialQuery, setViewInitialQuery] = useState('');
    const [recents, setRecents] = useState<RecentEntity[]>(() => recentsStore.load());
    const pageActionsRef = useRef<Map<symbol, PaletteActionDescriptor[]>>(new Map());
    const [pageActionsRev, setPageActionsRev] = useState(0);

    const { mode, query } = useMemo(() => parseMode(rawQuery), [rawQuery]);

    // setOpen / toggle are no-ops when the feature is disabled — keeps
    // `usePaletteActions` callers safe without ever showing palette UI.
    const setOpen = useCallback(
        (next: boolean) => {
            if (!enabled) return;
            setOpenState(next);
        },
        [enabled],
    );
    const toggle = useCallback(() => {
        if (!enabled) return;
        setOpenState((v) => !v);
    }, [enabled]);

    // Reset query and any open view each time the palette closes.
    useEffect(() => {
        if (!open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setRawQuery('');
            setActiveView(null);
        }
    }, [open]);

    // ⌘K / Ctrl+K hotkey, scoped to window. Skipped entirely when disabled
    // so the browser default keeps working.
    useEffect(() => {
        if (!enabled) return;
        function handler(e: KeyboardEvent) {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setOpenState((v) => !v);
            }
        }
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [enabled]);

    const pushRecent = useCallback((entry: Omit<RecentEntity, 'recordedAt'>) => {
        setRecents(recentsStore.push(entry));
    }, []);

    const removeRecent = useCallback((type: RecentEntity['type'], id: RecentEntity['id']) => {
        setRecents(recentsStore.remove(type, id));
    }, []);

    const clearRecents = useCallback(() => {
        recentsStore.clear();
        setRecents([]);
    }, []);

    const registerPageActions = useCallback((id: symbol, actions: PaletteActionDescriptor[]) => {
        pageActionsRef.current.set(id, actions);
        setPageActionsRev((v) => v + 1);
    }, []);

    const unregisterPageActions = useCallback((id: symbol) => {
        pageActionsRef.current.delete(id);
        setPageActionsRev((v) => v + 1);
    }, []);

    const openView = useCallback((id: string, initialQuery = '') => {
        setRawQuery('');
        setViewInitialQuery(initialQuery);
        setActiveView(id);
    }, []);

    const closeView = useCallback(() => setActiveView(null), []);

    const flatPageActions = useMemo<PaletteActionDescriptor[]>(() => {
        const out: PaletteActionDescriptor[] = [];
        // eslint-disable-next-line react-hooks/refs
        for (const list of pageActionsRef.current.values()) out.push(...list);
        return out;
        // pageActionsRev triggers recomputation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageActionsRev]);

    // Stable callbacks only — referentially constant across keystrokes.
    const api = useMemo<PaletteApiValue>(
        () => ({
            setOpen,
            toggle,
            setRawQuery,
            pushRecent,
            removeRecent,
            clearRecents,
            registerPageActions,
            unregisterPageActions,
            openView,
            closeView,
        }),
        [setOpen, toggle, setRawQuery, pushRecent, removeRecent, clearRecents, registerPageActions, unregisterPageActions, openView, closeView],
    );

    const value = useMemo<PaletteContextValue>(
        () => ({
            open,
            rawQuery,
            mode,
            query,
            recents,
            pageActions: flatPageActions,
            activeView,
            viewInitialQuery,
            setOpen,
            toggle,
            setRawQuery,
            pushRecent,
            removeRecent,
            clearRecents,
            registerPageActions,
            unregisterPageActions,
            openView,
            closeView,
        }),
        [
            open,
            rawQuery,
            mode,
            query,
            recents,
            flatPageActions,
            activeView,
            viewInitialQuery,
            setOpen,
            toggle,
            pushRecent,
            removeRecent,
            clearRecents,
            registerPageActions,
            unregisterPageActions,
            openView,
            closeView,
        ],
    );

    return (
        <PaletteApiContext.Provider value={api}>
            <PaletteContext.Provider value={value}>{children}</PaletteContext.Provider>
        </PaletteApiContext.Provider>
    );
}
